import React, { useEffectEvent, useRef } from 'react';
import { Row, Col, Button } from 'antd';
import { TextField, FormControlLabel, RadioGroup, Radio, IconButton, MenuItem, InputLabel, Select, FormControl } from '@mui/material';
import { Delete } from '@mui/icons-material';
import AudioRecorder from 'features/AudeoRecorderNew';
import AudioPicker from './AudioPicker';
import { v4 as uuidv4 } from 'uuid';
import Acent from 'components/Acent';
import COLOR from 'utils/constants/color';
import styled from 'styled-components';
const LABEL = 'abcdefghijklmnopqrstuvwxyz'.split('');
import TextEditor from 'components/TextEditor';
import { handleAudioTranscript } from 'store/submissions/thunks';

const Question = styled.div`
    color: ${COLOR.black100};
    display: block;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    margin-bottom:10px
`;

const AudioMultipleChoice = ({ questions = [], setQuestions, groupKey, setButtonDisabled, index }) => {
    const optionRefs = useRef({});
    const handleAudioChange = (value) => {
        if (value) {
            handleAudioTranscript(value);
        }
        setQuestions(questions.map(q => {
            if (q.groupKey === groupKey) {
                return { ...q, audio: value };
            }
            return q;
        }))
    };

    const handleAddQuestion = useEffectEvent(() => {
        const newQuestion = {
            id: uuidv4(),
            type: 'audioMultipleChoice',
            groupKey,
            audio: questions[0]?.audio,
            question: '',
            options: ['', '', '', ''],
            answer: 0,
        };
        const new_data = [...questions, newQuestion]
        setQuestions(new_data);
        setTimeout(() => {
            if (optionRefs.current[`${new_data.length - 1}-q`]) {
                optionRefs.current[`${new_data.length - 1}-q`].focus();
            }
        }, 500);
    });


    const handleQuestionChange = useEffectEvent((index, key, value) => {
        const updated = [...questions];
        updated[index][key] = value;
        setQuestions(updated);
    });
    const handleOptionChange = useEffectEvent((qIndex, optIndex, value) => {
        const updated = [...questions];
        if (!updated[qIndex].options) updated[qIndex].options = [];
        updated[qIndex].options[optIndex] = value;
        setQuestions(updated);
    });


    const handleOptionsLength = useEffectEvent((qIndex, newLength) => {
        const updated = [...questions];
        const opts = updated[qIndex].options || [];
        if (opts.length < newLength) {
            updated[qIndex].options = [...opts, ...new Array(newLength - opts.length).fill('')];
        } else {
            updated[qIndex].options = opts.slice(0, newLength);
            if (updated[qIndex].answer >= newLength) updated[qIndex].answer = 0;
        }
        setQuestions(updated);
    });

    const handleDeleteQuestion = useEffectEvent((index) => {
        const updated = [...questions];
        updated.splice(index, 1);
        setQuestions(updated);
    });
    return (
        <div style={{ border: '1px dashed #aaa', padding: '16px', borderRadius: '8px' }}>
            <AudioRecorder
                handleAudio={handleAudioChange}
                index={index}
                currentSubmission={{}}
                refresh={() => { }}
                source={questions[0]?.audio}
                hasSaveButton
                message={`Record question audio`}
                setButtonDisabled={(value) => setButtonDisabled(value)}
            />
            <p style={{ textAlign: 'center' }}>OR</p>
            <AudioPicker onChange={handleAudioChange}
                message={"Upload a audio file for question..."}
                setButtonDisabled={(value) => setButtonDisabled(value)}
            />

            {questions.map((q, index) => (
                <div key={index} style={{ marginTop: '24px', borderTop: '1px solid #ddd', paddingTop: '16px' }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <h4>Instruction {index + 1}*</h4>
                        </Col>
                        <Col>
                            {questions.length <= 1 ? null : <IconButton onClick={() => handleDeleteQuestion(index)}>
                                <Delete color="secondary" />
                            </IconButton>}
                        </Col>
                    </Row>
                    <Question style={{ marginTop: 6 }}>
                        <label style={{ marginBottom: 0, fontSize: 12, fontWeight: 400 }}>Question*</label>
                        <TextEditor
                            handleChange={(value) => {
                                handleQuestionChange(index, "question", value)
                            }}
                            value={q.question || ''}
                            autoFocus={false}
                            disabled={false}
                            placeholder="Type your question here..."
                            containerClassName='audio-multichoice-question-field'
                        />
                    </Question>
                    <FormControl variant="outlined" style={{ marginBottom: 16, minWidth: 120 }}>
                        <InputLabel id={`options-length-${index}`}>Number of options</InputLabel>
                        <Select
                            labelId={`options-length-${index}`}
                            value={q.options?.length || 4}
                            onChange={(e) => handleOptionsLength(index, parseInt(e.target.value))}
                            label="Number of options"
                        >
                            {[2, 3, 4, 5].map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <RadioGroup
                        value={String(q.answer)}
                        onChange={(e) => handleQuestionChange(index, 'answer', parseInt(e.target.value))}
                    >
                        {(q.options || []).map((opt, optIdx) => (
                            <Row key={optIdx} style={{ alignItems: 'center', marginBottom: '8px' }}>
                                <Col span={2}>
                                    <FormControlLabel
                                        value={String(optIdx)}
                                        control={<Radio />}
                                        label={''}
                                    />
                                </Col>
                                <Col span={22}>
                                    <Acent
                                        inputRef={optionRefs.current[`${index}-${optIdx}`]}
                                        setValue={(v) => handleOptionChange(index, optIdx, v)}
                                        style={{ marginBottom: 5, display: 'flex', justifyContent: 'flex-end' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label={`Option ${LABEL[optIdx]}`}
                                        className={"assignment-option"}
                                        InputLabelProps={{ shrink: true, required: true }}
                                        type="text"
                                        value={opt}
                                        onChange={(e) => handleOptionChange(index, optIdx, e.target.value)}
                                        variant="outlined"
                                        inputRef={(ref) => (optionRefs.current[`${index}-${optIdx}`] = ref)}
                                    />
                                </Col>
                            </Row>
                        ))}
                    </RadioGroup>
                </div>
            ))}

            <Button
                type="primary"
                onClick={handleAddQuestion}
                // disabled={!questions[0]?.audio}
                style={{ marginTop: '16px' }}
            >
                Add Sub-Question
            </Button>
        </div>
    );
};

export default AudioMultipleChoice;